// Floating chat widget for demo pages.
// Same pattern as RentingPilot's chat — sits bottom-right, opens a panel,
// shows preset greeting + typing indicator + scripted replies.
// Multi-language: reads the same Dict i18n as the surrounding demo.

import { useState, useRef, useEffect } from 'react'
import { useT } from './i18n-ctx'
import type { Dict } from './i18n-ctx'

const dict: Dict = {
  chat_title: { en: 'Chat with us', mk: 'Разговор со нас', gr: 'Συνομιλία μαζί μας' },
  chat_subtitle: { en: 'Average reply: 2 minutes', mk: 'Просечен одговор: 2 минути', gr: 'Μέσος χρόνος απάντησης: 2 λεπτά' },
  open_chat: { en: 'Chat', mk: 'Чат', gr: 'Chat' },
  close: { en: 'Close', mk: 'Затвори', gr: 'Κλείσιμο' },
  placeholder: { en: 'Type your message…', mk: 'Твоја порака…', gr: 'Γράψε μήνυμα…' },
  send: { en: 'Send', mk: 'Испрати', gr: 'Αποστολή' },
  bot_greeting: {
    en: "Hi! 👋 I'm an AI assistant. Ask about bookings, prices, hours — or leave a message and a human will reply within minutes.",
    mk: 'Здраво! 👋 Јас сум AI асистент. Прашај за термини, цени, работно време — или остави порака и човек ќе ти одговори.',
    gr: 'Γεια! 👋 Είμαι AI βοηθός. Ρώτα για κρατήσεις, τιμές, ώρες — ή άφησε μήνυμα.',
  },
  bot_reply: {
    en: 'Thanks! A team member will reply shortly. In the meantime, want to book directly?',
    mk: 'Благодарам! Ќе ти одговориме наскоро. Во меѓувреме, сакаш да резервираш директно?',
    gr: 'Ευχαριστώ! Κάποιος θα σου απαντήσει σύντομα. Θες να κάνεις κράτηση;',
  },
  typing: { en: 'Typing…', mk: 'Пишува…', gr: 'Γράφει…' },
  suggestions_title: { en: 'Quick questions', mk: 'Брзи прашања', gr: 'Γρήγορες ερωτήσεις' },
  sug_prices: { en: 'What are your prices?', mk: 'Кои се цените?', gr: 'Ποιες είναι οι τιμές;' },
  sug_hours: { en: 'What are your hours?', mk: 'Кога работите?', gr: 'Τι ώρες είστε ανοιχτά;' },
  sug_booking: { en: 'How do I book?', mk: 'Како да резервирам?', gr: 'Πώς κλείνω;' },
  sug_languages: { en: 'Do you speak English?', mk: 'Дали зборувате англиски?', gr: 'Μιλάτε αγγλικά;' },
  ans_prices: { en: 'Prices start from €5. See the service list on the left for all options.', mk: 'Цените почнуваат од 5€. Види список на услуги.', gr: 'Τιμές από 5€.' },
  ans_hours: { en: 'Monday–Saturday 09:00–19:00. Closed Sundays.', mk: 'Понеделник–Сабота 09:00–19:00. Неделата не работиме.', gr: 'Δευτέρα–Σάββατο 09:00–19:00.' },
  ans_booking: { en: 'Use the booking form on this page — takes 30 seconds.', mk: 'Користи го формуларот на оваа страница — 30 секунди.', gr: 'Χρησιμοποίησε τη φόρμα — 30 δευτερόλεπτα.' },
  ans_languages: { en: 'Yes — English, Macedonian, and Greek. Switch language at the top.', mk: 'Да — англиски, македонски и грчки.', gr: 'Ναι — αγγλικά, μακεδονικά, ελληνικά.' },
  powered: { en: 'Powered by Qordo Catalog', mk: 'Поддржано од Qordo Catalog', gr: 'Από Qordo Catalog' },
}

export const CHAT_DICT = dict

type Message = { role: 'bot' | 'user'; text: string; key?: string }
type Lang = 'en' | 'mk' | 'gr'

// ─────────────────── Persona-aware reply engine ───────────────────
// A persona makes the bot answer in-character for a specific business and
// simulate a real booking / order / quote — instead of the generic fallback.
export type ChatPersona = {
  /** Localised business/industry name, e.g. "Beauty Spa". */
  name: string
  /** Shapes the simulated action wording. */
  kind: 'booking' | 'order' | 'quote'
  /** Real services / menu / offerings with optional price. */
  items: { name: string; price?: number }[]
  /** Optional localised opening hours line. */
  hours?: string
}

const PHRASES = {
  greeting: {
    en: (n: string) => `Hi! 👋 Welcome to ${n}. I can tell you prices and opening hours — or take a booking for you right here. What do you need?`,
    mk: (n: string) => `Здраво! 👋 Добreddojde во ${n}. Можам да ти кажам цени и работно време — или да резервирам за тебе тука. Што ти треба?`,
    gr: (n: string) => `Γεια! 👋 Καλώς ήρθες στο ${n}. Μπορώ να σου πω τιμές και ώρες — ή να κλείσω κράτηση εδώ. Τι χρειάζεσαι;`,
  },
  priceIntro: {
    en: 'Here are some of our options:',
    mk: 'Еве некои од нашите опции:',
    gr: 'Ορίστε μερικές επιλογές:',
  },
  hoursDefault: {
    en: 'We are open Monday–Saturday, 09:00–19:00. Closed Sundays.',
    mk: 'Работиме понеделник–сабота, 09:00–19:00. Неделата затворено.',
    gr: 'Ανοιχτά Δευτέρα–Σάββατο, 09:00–19:00. Κυριακή κλειστά.',
  },
  whichItem: {
    booking: { en: 'Of course — which service would you like to book?', mk: 'Се разбира — која услуга сакаш да резервираш?', gr: 'Φυσικά — ποια υπηρεσία θες να κλείσεις;' },
    order: { en: 'Great — what would you like to order?', mk: 'Одлично — што сакаш да нарачаш?', gr: 'Τέλεια — τι θες να παραγγείλεις;' },
    quote: { en: 'Sure — what would you like a quote for?', mk: 'Се разбира — за што сакаш понуда?', gr: 'Βεβαίως — για τι θες προσφορά;' },
  },
  confirmQ: {
    booking: { en: (i: string, p: string) => `Got it — ${i}${p}. Shall I book it for you? (yes / no)`, mk: (i: string, p: string) => `Во ред — ${i}${p}. Да резервирам? (да / не)`, gr: (i: string, p: string) => `Εντάξει — ${i}${p}. Να το κλείσω; (ναι / όχι)` },
    order: { en: (i: string, p: string) => `Got it — ${i}${p}. Shall I place the order? (yes / no)`, mk: (i: string, p: string) => `Во ред — ${i}${p}. Да ja нарачам? (да / не)`, gr: (i: string, p: string) => `Εντάξει — ${i}${p}. Να την καταχωρήσω; (ναι / όχι)` },
    quote: { en: (i: string, p: string) => `Got it — ${i}${p}. Shall I send the quote request? (yes / no)`, mk: (i: string, p: string) => `Во ред — ${i}${p}. Да ja испратам понудата? (да / не)`, gr: (i: string, p: string) => `Εντάξει — ${i}${p}. Να στείλω το αίτημα; (ναι / όχι)` },
  },
  done: {
    booking: { en: (i: string, r: string) => `✅ Booked! Your appointment for ${i} is confirmed. Ref ${r}. You'd get an SMS reminder 24h before. (This is a demo — no real booking was made.)`, mk: (i: string, r: string) => `✅ Резервирано! Терминот за ${i} е потврден. Реф ${r}. (Демо — нема вистинска резервација.)`, gr: (i: string, r: string) => `✅ Κλείστηκε! Το ραντεβού για ${i} επιβεβαιώθηκε. Κωδ. ${r}. (Demo — καμία πραγματική κράτηση.)` },
    order: { en: (i: string, r: string) => `✅ Order placed! ${i} — ready in ~30 min. Ref ${r}. (This is a demo — nothing was really ordered.)`, mk: (i: string, r: string) => `✅ Нарачано! ${i} — готово за ~30 мин. Реф ${r}. (Демо — ништо не е навистина нарачано.)`, gr: (i: string, r: string) => `✅ Καταχωρήθηκε! ${i} — έτοιμο σε ~30'. Κωδ. ${r}. (Demo — τίποτα πραγματικό.)` },
    quote: { en: (i: string, r: string) => `✅ Request sent! You'll get a quote for ${i} within 24h. Ref ${r}. (This is a demo — no real request was sent.)`, mk: (i: string, r: string) => `✅ Испратено! Ќе добиеш понуда за ${i} за 24ч. Реф ${r}. (Демо — нема вистинско барање.)`, gr: (i: string, r: string) => `✅ Στάλθηκε! Θα λάβεις προσφορά για ${i} σε 24ω. Κωδ. ${r}. (Demo — κανένα πραγματικό αίτημα.)` },
  },
  cancelled: {
    en: 'No problem — tell me if you change your mind, or ask about anything else.',
    mk: 'Нема проблем — кажи ми ако се предомислиш, или прашај друго.',
    gr: 'Κανένα πρόβλημα — πες μου αν αλλάξεις γνώμη, ή ρώτα κάτι άλλο.',
  },
  fallback: {
    booking: { en: 'I can help with prices, opening hours, or booking an appointment. What would you like?', mk: 'Можам да помогнам со цени, работно време или резервација. Што сакаш?', gr: 'Μπορώ να βοηθήσω με τιμές, ώρες ή κράτηση. Τι θες;' },
    order: { en: 'I can help with the menu, prices, delivery, or placing an order. What would you like?', mk: 'Можам да помогнам со мени, цени, достава или нарачка. Што сакаш?', gr: 'Μπορώ να βοηθήσω με μενού, τιμές, delivery ή παραγγελία. Τι θες;' },
    quote: { en: 'I can help with our services, response time, or requesting a quote. What would you like?', mk: 'Можам да помогнам со услуги, време на одговор или понуда. Што сакаш?', gr: 'Μπορώ να βοηθήσω με υπηρεσίες, χρόνο απάντησης ή προσφορά. Τι θες;' },
  },
} as const

const KW = {
  price: ['price', 'cost', 'how much', 'expensive', 'cheap', 'цена', 'цени', 'колку', 'τιμή', 'τιμές', 'κοστ'],
  hours: ['hour', 'open', 'close', 'when', 'time you', 'работно', 'отворено', 'кога', 'ώρα', 'ώρες', 'ανοιχτ'],
  act: ['book', 'reserve', 'appointment', 'order', 'buy', 'deliver', 'quote', 'request', 'резерв', 'нарач', 'купи', 'достав', 'понуд', 'бар', 'κράτη', 'κλεισ', 'παραγγ', 'προσφορ'],
  yes: ['yes', 'yeah', 'yep', 'ok', 'okay', 'sure', 'confirm', 'да', 'важи', 'ναι', 'οκ', 'ενταξ'],
  no: ['no', 'nope', 'cancel', 'не', 'откажи', 'όχι', 'ακυρ'],
}
const hit = (text: string, list: readonly string[]) => list.some((k) => text.includes(k))

function priceList(persona: ChatPersona): string {
  return persona.items
    .slice(0, 5)
    .map((i) => `• ${i.name}${i.price != null ? (i.price === 0 ? ' — Free' : ` — €${i.price}`) : ''}`)
    .join('\n')
}
const refNo = () => `#QD-${Math.floor(1000 + Math.random() * 9000)}`

/** Returns the bot reply + the new pending-item state. Pure, scripted, no network. */
function personaReply(
  raw: string,
  persona: ChatPersona,
  lang: Lang,
  pending: string | null,
): { reply: string; pending: string | null } {
  const text = raw.toLowerCase().trim()
  const matchedItem = persona.items.find((i) => text.includes(i.name.toLowerCase().split(' ')[0]) && i.name.length > 2)

  // Mid-flow: awaiting yes/no on a pending item
  if (pending) {
    if (hit(text, KW.yes)) return { reply: PHRASES.done[persona.kind][lang](pending, refNo()), pending: null }
    if (hit(text, KW.no)) return { reply: PHRASES.cancelled[lang], pending: null }
    if (matchedItem) {
      const p = matchedItem.price != null ? (matchedItem.price === 0 ? ' (Free)' : ` (€${matchedItem.price})`) : ''
      return { reply: PHRASES.confirmQ[persona.kind][lang](matchedItem.name, p), pending: matchedItem.name }
    }
    // treat anything else as confirmation nudge
    return { reply: PHRASES.confirmQ[persona.kind][lang](pending, ''), pending }
  }

  if (hit(text, KW.price)) return { reply: `${PHRASES.priceIntro[lang]}\n${priceList(persona)}`, pending: null }
  if (hit(text, KW.hours)) return { reply: persona.hours ?? PHRASES.hoursDefault[lang], pending: null }

  if (matchedItem) {
    const p = matchedItem.price != null ? (matchedItem.price === 0 ? ' (Free)' : ` (€${matchedItem.price})`) : ''
    return { reply: PHRASES.confirmQ[persona.kind][lang](matchedItem.name, p), pending: matchedItem.name }
  }
  if (hit(text, KW.act)) return { reply: PHRASES.whichItem[persona.kind][lang], pending: null }

  return { reply: PHRASES.fallback[persona.kind][lang], pending: null }
}

function personaSuggestions(persona: ChatPersona, lang: Lang): string[] {
  // Darinka pattern: lead with the actual services as tappable chips, then
  // the generic price/hours questions. Tapping a service name goes straight
  // into the booking confirm flow via personaReply's item matching.
  const price = { en: 'What are your prices?', mk: 'Кои се цените?', gr: 'Ποιες είναι οι τιμές;' }
  const hours = { en: 'What are your opening hours?', mk: 'Кое е работното време?', gr: 'Τι ώρες είστε ανοιχτά;' }
  const items = persona.items.slice(0, 4).map((i) => i.name)
  return [...items, price[lang], hours[lang]]
}

export default function ChatWidget({ persona }: { persona?: ChatPersona }) {
  const { t, lang } = useT()
  const safeLang: Lang = (['en', 'mk', 'gr'] as const).includes(lang as Lang) ? (lang as Lang) : 'en'
  const [open, setOpen] = useState(false)
  const [unread, setUnread] = useState(1)
  const [input, setInput] = useState('')
  const [typing, setTyping] = useState(false)
  const [pending, setPending] = useState<string | null>(null)
  const initial: Message[] = persona
    ? [{ role: 'bot', text: PHRASES.greeting[safeLang](persona.name) }]
    : [{ role: 'bot', key: 'bot_greeting', text: '' }]
  const [messages, setMessages] = useState<Message[]>(initial)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  // Resolve the bot greeting in the current language on first open
  useEffect(() => {
    setMessages((ms) =>
      ms.map((m, i) =>
        m.key ? { ...m, text: t(m.key) } : persona && i === 0 && m.role === 'bot' ? { ...m, text: PHRASES.greeting[safeLang](persona.name) } : m,
      ),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang])

  useEffect(() => {
    if (open) setUnread(0)
  }, [open])

  // Darinka pattern: the assistant opens itself shortly after load — the
  // conversation IS the demo. One-shot; the visitor can close it any time.
  useEffect(() => {
    const id = window.setTimeout(() => setOpen(true), 1400)
    return () => window.clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 999_999, behavior: 'smooth' })
  }, [messages, typing])

  function sendUser(text: string) {
    if (!text.trim()) return
    setMessages((ms) => [...ms, { role: 'user', text }])
    setInput('')
    setTyping(true)
    window.setTimeout(() => {
      setTyping(false)
      if (persona) {
        const { reply, pending: next } = personaReply(text, persona, safeLang, pending)
        setPending(next)
        setMessages((ms) => [...ms, { role: 'bot', text: reply }])
      } else {
        setMessages((ms) => [...ms, { role: 'bot', key: 'bot_reply', text: t('bot_reply') }])
      }
      if (!open) setUnread((n) => n + 1)
    }, 1000)
  }

  function sendSuggestion(q: string, a: string) {
    setMessages((ms) => [...ms, { role: 'user', text: q }])
    setTyping(true)
    window.setTimeout(() => {
      setTyping(false)
      setMessages((ms) => [...ms, { role: 'bot', text: a }])
    }, 900)
  }

  return (
    <>
      {/* Launcher button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={t('open_chat')}
          className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 shadow-xl shadow-brand-600/30 text-white grid place-items-center hover:scale-105 transition-transform"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path d="M12 2C6.48 2 2 5.92 2 10.75c0 2.66 1.4 5.04 3.6 6.63v3.87l3.64-2.17c.9.2 1.83.31 2.76.31 5.52 0 10-3.92 10-8.64C22 5.92 17.52 2 12 2z" />
          </svg>
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-red-500 text-[11px] font-bold grid place-items-center">
              {unread}
            </span>
          )}
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[380px] sm:h-[580px] z-50 flex flex-col bg-ink-950 border border-ink-800 sm:rounded-2xl shadow-2xl overflow-hidden">
          <header className="bg-gradient-to-br from-brand-600 to-brand-900 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-white/20 grid place-items-center text-xl">💬</div>
                <div>
                  <div className="font-semibold text-sm">{t('chat_title')}</div>
                  <div className="text-xs text-white/70 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    {t('chat_subtitle')}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label={t('close')}
                className="p-2 rounded-lg hover:bg-white/10"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </header>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-ink-900/40">
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} text={m.text} />
            ))}
            {typing && <TypingBubble label={t('typing')} />}

            {/* Quick suggestions after the greeting */}
            {messages.length === 1 && (
              <div className="pt-2">
                <div className="text-[11px] uppercase tracking-wide text-ink-500 mb-2">{t('suggestions_title')}</div>
                <div className="flex flex-wrap gap-2">
                  {persona ? (
                    personaSuggestions(persona, safeLang).map((s, i) => (
                      <button
                        key={i}
                        onClick={() => sendUser(s)}
                        className="text-xs rounded-full bg-ink-800 border border-ink-800 text-ink-100 px-3 py-1.5 hover:border-brand-500"
                      >
                        {s}
                      </button>
                    ))
                  ) : (
                    (['prices', 'hours', 'booking', 'languages'] as const).map((k) => (
                      <button
                        key={k}
                        onClick={() => sendSuggestion(t(`sug_${k}`), t(`ans_${k}`))}
                        className="text-xs rounded-full bg-ink-800 border border-ink-800 text-ink-100 px-3 py-1.5 hover:border-brand-500"
                      >
                        {t(`sug_${k}`)}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => {
              e.preventDefault()
              sendUser(input)
            }}
            className="border-t border-ink-800 p-3 flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('placeholder')}
              className="input py-2 text-sm flex-1"
              aria-label={t('placeholder')}
            />
            <button type="submit" className="btn-primary text-sm py-2 px-4">{t('send')}</button>
          </form>
          <div className="text-[10px] text-ink-500 text-center py-1.5 border-t border-ink-800 bg-ink-950">
            {t('powered')}
          </div>
        </div>
      )}
    </>
  )
}

function Bubble({ role, text }: { role: 'bot' | 'user'; text: string }) {
  const isUser = role === 'user'
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
          isUser
            ? 'bg-brand-600 text-white rounded-br-sm'
            : 'bg-ink-800 text-ink-100 rounded-bl-sm'
        }`}
      >
        {text}
      </div>
    </div>
  )
}

function TypingBubble({ label }: { label: string }) {
  return (
    <div className="flex justify-start" aria-label={label}>
      <div className="bg-ink-800 text-ink-100 rounded-2xl rounded-bl-sm px-3 py-2 text-sm">
        <div className="flex items-center gap-1 h-5">
          <span className="w-1.5 h-1.5 bg-ink-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-1.5 h-1.5 bg-ink-500 rounded-full animate-bounce" style={{ animationDelay: '120ms' }} />
          <span className="w-1.5 h-1.5 bg-ink-500 rounded-full animate-bounce" style={{ animationDelay: '240ms' }} />
        </div>
      </div>
    </div>
  )
}
